"use client"

import { useMutation, useQueryClient } from "@tanstack/react-query"
import { usePermissions } from "@/hooks/use-permissions"
import type { AttributionModelKey } from "@/lib/attribution/attribution-models"
import {
  setAttributionModelPreferenceAction,
  type AttributionModelPreference,
} from "@/server-actions/shopify/attribution-model-preference"
import { attributionModelPreferenceQueryKey } from "@/hooks/queries/attribution-model-preference"
import { useTranslatedQueryHandling } from "@/hooks/use-translated-query-handling"

import { useTranslations } from "next-intl"


/** The cache is the dropdown's state: it switches at once and rolls back only if the save fails. */
export function useSetAttributionModelPreferenceMutation() {
  const withQueryHandling = useTranslatedQueryHandling()

  const tUi = useTranslations("Feedback.MutationsAttributionModelPreference")

  const queryClient = useQueryClient()
  const { activeTenantId } = usePermissions()
  const queryKey = attributionModelPreferenceQueryKey(activeTenantId)

  return useMutation({
    mutationFn: async (model: AttributionModelKey): Promise<AttributionModelPreference> => {
      const result = await withQueryHandling(setAttributionModelPreferenceAction({ model }), {
        showLoadingToast: false,
        showSuccessToast: false,
        error: tUi("theAttributionModelCouldNotBeSaved"),
      })
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
    onMutate: async (model) => {
      await queryClient.cancelQueries({ queryKey })
      const previous = queryClient.getQueryData<AttributionModelPreference>(queryKey)
      queryClient.setQueryData<AttributionModelPreference>(queryKey, { model })
      return { previous }
    },
    onError: async (_error, _model, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous)
      } else {
        // Nothing to restore (the first read had failed): drop the optimistic value and read again.
        await queryClient.resetQueries({ queryKey })
      }
    },
    onSuccess: (data) => {
      queryClient.setQueryData(queryKey, data)
    },
  })
}
